import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import {
  api,
  type AlertKind,
  type AlertState,
  type AlertView,
  type Severity,
  type TenantAlertCount,
} from './api.ts';
import './alerts.css';

/**
 * Alerts — what has moved backwards, across every client.
 *
 * A scheduled re-assessment produces drift; drift that matters produces an
 * alert. This screen is the whole of what an MSP engineer does with them:
 * see what regressed, for which client, and record that somebody has picked
 * it up.
 *
 * **The three kinds are not the same thing and must never look the same.** A
 * `regression` is the client getting worse and is the one worth interrupting
 * somebody for. `visibility_lost` means we stopped being able to assess a
 * control — nearly always a licence lapse or a narrowed consent — and it is not
 * a finding about that client's security at all. Drawn in the failure register
 * it would tell an MSP their customer has a security problem when what they
 * have is a billing problem, so it takes the violet "not evidenced" family used
 * everywhere else for "we could not find out", and it is visibly quieter.
 *
 * `excess_permission` is not about the client at all. It says DriftWatch holds
 * a permission in their directory that this build no longer asks for — our
 * privilege, their remedy, because removing an app role assignment is a write
 * and no code path in this product may perform one. It has no control, no
 * verdicts and nothing that moved, and the row says so rather than borrowing a
 * pair of words from the kinds that do.
 *
 * **`summary` is product copy written on the server and is rendered verbatim.**
 * The alert record, the API and this screen have to say the same sentence about
 * the same alert; a console that paraphrased it would be a fourth opinion.
 */

const SEVERITY_TEXT: Record<Severity, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const SEVERITY_RANK: Record<Severity, number> = { critical: 0, high: 1, medium: 2, low: 3 };

interface KindStyle {
  readonly label: string;
  readonly chip: string;
  readonly row: string;
}

const KIND: Record<AlertKind, KindStyle> = {
  regression: { label: 'Regression', chip: 'al-kind-regression', row: 'al-row-regression' },
  visibility_lost: { label: 'Visibility lost', chip: 'al-kind-visibility', row: 'al-row-visibility' },
  excess_permission: {
    // Named for what it is. Not "excess privilege", which sounds like a finding
    // about the client, and not "permission drift", which it is not: nothing in
    // the client's directory moved.
    label: 'Permission we hold',
    chip: 'al-kind-excess',
    row: 'al-row-excess',
  },
};

const KINDS: readonly AlertKind[] = ['regression', 'visibility_lost', 'excess_permission'];

/**
 * What this alert is about, in one line, for a sort key and for a screen reader.
 *
 * A control alert is about a control and an excess permission is about a
 * permission. There is no field both have, and reaching for `controlTitle` on
 * an alert that has none is the failure the union exists to make impossible —
 * so the branch is here, once, rather than at each of the four places that
 * needs a label.
 */
const subjectOf = (alert: AlertView): { readonly title: string; readonly id: string } =>
  alert.kind === 'excess_permission'
    ? { title: alert.permission, id: alert.api }
    : { title: alert.controlTitle, id: alert.controlId };

/**
 * The badge each verdict word draws, so an alert describes a verdict exactly as
 * the report and the findings screen do. A word this table does not know is
 * rendered as plain text rather than mapped to the nearest badge — the console
 * does not decide verdicts, and guessing one wrong is worse than showing none.
 */
const VERDICT_CLASS: Record<string, string> = {
  Met: 'verdict-met',
  'Not met': 'verdict-not-met',
  'Not evidenced': 'verdict-not-evidenced',
  'Not applicable': 'verdict-not-applicable',
};

type StateFilter = 'live' | 'open' | 'acknowledged' | 'resolved';

/** What each filter asks the server for. The default excludes resolved. */
const STATE_QUERY: Record<StateFilter, readonly AlertState[]> = {
  live: ['open', 'acknowledged'],
  open: ['open'],
  acknowledged: ['acknowledged'],
  resolved: ['resolved'],
};

const STATE_FILTERS: ReadonlyArray<{ readonly id: StateFilter; readonly label: string }> = [
  { id: 'live', label: 'Open and acknowledged' },
  { id: 'open', label: 'Open only' },
  { id: 'acknowledged', label: 'Acknowledged only' },
  { id: 'resolved', label: 'Resolved' },
];

/** The operator's name, kept so eight acknowledgements are not eight typings. */
const WHO_KEY = 'driftwatch-acknowledged-by';

const readWho = (): string => {
  try {
    return window.localStorage.getItem(WHO_KEY) ?? '';
  } catch {
    return '';
  }
};

const writeWho = (value: string): void => {
  try {
    window.localStorage.setItem(WHO_KEY, value);
  } catch {
    // A browser that blocks storage costs one retyped name. It must not cost
    // the screen.
  }
};

const message = (cause: unknown): string =>
  cause instanceof Error ? cause.message : String(cause);

const plural = (count: number, noun: string): string => `${count} ${noun}${count === 1 ? '' : 's'}`;

const DAY_MS = 86_400_000;

const time = (iso: string): number => {
  const value = Date.parse(iso);
  return Number.isNaN(value) ? Number.NaN : value;
};

const asDate = (iso: string): string => {
  const value = time(iso);
  return Number.isNaN(value) ? 'Unknown' : new Date(value).toLocaleDateString('en-GB');
};

const relative = (iso: string, now: number): string => {
  const value = time(iso);
  if (Number.isNaN(value)) return 'Unknown';
  const days = Math.floor((now - value) / DAY_MS);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 14) return `${days} days ago`;
  if (days < 60) return `${Math.round(days / 7)} weeks ago`;
  return `${Math.round(days / 30)} months ago`;
};

/** Newest still true first: the alert most recently confirmed leads the list. */
const order = (a: AlertView, b: AlertView): number => {
  const recency = (time(b.lastObservedAt) || 0) - (time(a.lastObservedAt) || 0);
  if (recency !== 0) return recency;
  const severity = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
  if (severity !== 0) return severity;
  return (
    a.tenantDisplayName.localeCompare(b.tenantDisplayName, 'en-GB') ||
    subjectOf(a).title.localeCompare(subjectOf(b).title, 'en-GB')
  );
};

const matchesState = (alert: AlertView, filter: StateFilter): boolean =>
  STATE_QUERY[filter].includes(alert.state);

const replace = (list: readonly AlertView[], next: AlertView): AlertView[] =>
  list.map((alert) => (alert.id === next.id ? next : alert));

/**
 * The estate figures are the server's tallies, not a count of whatever the
 * current filter happens to be showing. Where a deployment has sent none, they
 * fall back to the loaded alerts: zero above a visible list of alerts reads as
 * a broken screen, which is the one thing these tiles must never do.
 */
const tally = (
  counts: readonly TenantAlertCount[],
  alerts: readonly AlertView[],
): { open: number; acknowledged: number } => {
  if (counts.length > 0) {
    return counts.reduce(
      (total, row) => ({
        open: total.open + row.open,
        acknowledged: total.acknowledged + row.acknowledged,
      }),
      { open: 0, acknowledged: 0 },
    );
  }
  return {
    open: alerts.filter((alert) => alert.state === 'open').length,
    acknowledged: alerts.filter((alert) => alert.state === 'acknowledged').length,
  };
};

/** One alert moving from open to acknowledged, without a second round trip. */
const shiftAcknowledged = (
  counts: readonly TenantAlertCount[],
  tenantId: string,
): TenantAlertCount[] =>
  counts.map((row) =>
    row.tenantId === tenantId
      ? { ...row, open: Math.max(0, row.open - 1), acknowledged: row.acknowledged + 1 }
      : row,
  );

export const Alerts = () => {
  const [alerts, setAlerts] = useState<AlertView[]>([]);
  const [counts, setCounts] = useState<TenantAlertCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stateFilter, setStateFilter] = useState<StateFilter>('live');
  const [kinds, setKinds] = useState<ReadonlySet<AlertKind>>(new Set());
  const [who, setWho] = useState<string>(readWho);
  const [pending, setPending] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const whoRef = useRef<HTMLInputElement>(null);

  /**
   * A screen reader does not re-announce a live region whose text is unchanged,
   * and acknowledging two alerts in a row produces near-identical sentences.
   * The alternating zero-width space guarantees a change without a visible one.
   */
  const announce = useCallback((text: string) => {
    setAnnouncement((previous) => (previous.endsWith('\u200B') ? text : `${text}\u200B`));
  }, []);

  const load = useCallback(
    async (filter: StateFilter, announceResult: boolean) => {
      setBusy(true);
      try {
        const response = await api.listAlerts(STATE_QUERY[filter]);
        setAlerts(response.alerts);
        setCounts(response.counts);
        setError(null);
        if (announceResult) announce(`${plural(response.alerts.length, 'alert')} loaded.`);
      } catch (cause) {
        // Framed rather than passed through. The server's own words are often a
        // bare code — "unauthorised", "Not Found" — and a bare code on its own
        // above an empty screen does not say which thing failed.
        setError(`The alerts could not be loaded. ${message(cause)}`);
      } finally {
        setBusy(false);
        setLoading(false);
      }
    },
    [announce],
  );

  useEffect(() => {
    void load(stateFilter, false);
  }, [load, stateFilter]);

  const acknowledge = async (alert: AlertView) => {
    const name = who.trim();
    if (name === '') {
      whoRef.current?.focus();
      announce('Enter your name before acknowledging an alert.');
      return;
    }

    // Optimistic, then reconciled: the row shows the outcome at once, the
    // server's own record replaces it, and a refusal puts the list back exactly
    // as it was rather than leaving an acknowledgement nobody recorded.
    const snapshot = alerts;
    setPending(alert.id);
    setAlerts((current) =>
      replace(current, {
        ...alert,
        state: 'acknowledged',
        acknowledgedBy: name,
        acknowledgedAt: new Date().toISOString(),
      }),
    );

    try {
      const response = await api.acknowledgeAlert(alert.tenantId, alert.id, name);
      setAlerts((current) => replace(current, response.alert));
      setCounts((current) => shiftAcknowledged(current, alert.tenantId));
      setError(null);
      writeWho(name);
      announce(`Acknowledged ${subjectOf(alert).title} for ${alert.tenantDisplayName}, as ${name}.`);
    } catch (cause) {
      setAlerts(snapshot);
      setError(`That alert was not acknowledged, and nothing was recorded. ${message(cause)}`);
      announce('That alert could not be acknowledged. Nothing was recorded.');
    } finally {
      setPending(null);
    }
  };

  const now = Date.now();
  const inState = alerts.filter((alert) => matchesState(alert, stateFilter));
  const visible = [...inState]
    .filter((alert) => kinds.size === 0 || kinds.has(alert.kind))
    .sort(order);
  /*
   * Derived from `KINDS` rather than written out, so a fourth kind cannot arrive
   * with a chip that reads zero for ever. The third one nearly did.
   */
  const kindCounts = Object.fromEntries(
    KINDS.map((kind) => [kind, inState.filter((alert) => alert.kind === kind).length]),
  ) as Record<AlertKind, number>;
  const figures = tally(counts, alerts);
  const affected = new Set(inState.map((alert) => alert.tenantId)).size;
  const filtered = kinds.size > 0;
  const blind = visible.filter((alert) => alert.kind === 'visibility_lost').length;
  /**
   * A failed read has no figures and no empty state. Tiles of zeros and "no
   * alerts — nothing has moved backwards" beneath an error strip contradict the
   * error and each other: one says everything is well, the other says we do not
   * know. A refused acknowledgement is a different case — the alerts on screen
   * were read successfully and stay visible.
   */
  const loaded = error === null || alerts.length > 0;

  const toggleKind = (kind: AlertKind) => {
    const next = new Set(kinds);
    if (!next.delete(kind)) next.add(kind);
    setKinds(next);
  };

  return (
    <div className="alerts-screen">
      <p className="lead">
        A control that has moved backwards since a client&rsquo;s previous assessment, and the ones
        we can no longer assess at all. Newest still true first, across every client you manage.
      </p>

      <p aria-live="polite" role="status" className="sr-only">
        {announcement}
      </p>

      {error !== null && <div className="error">{error}</div>}

      {/* Counts, never proportions: three absolute figures an auditor could
          recount by hand. A share of a whole would invite the reader to chase
          the ratio instead of the clients behind it (ADR-0007). */}
      {!loading && loaded && (
        <div className="stat-grid al-summary">
          <div className="stat">
            <span className="stat-figure">{figures.open}</span>
            <span className="stat-label">Open</span>
            <span className="stat-hint">Nobody has picked these up yet</span>
          </div>
          <div className="stat">
            <span className="stat-figure">{figures.acknowledged}</span>
            <span className="stat-label">Acknowledged</span>
            <span className="stat-hint">Someone has taken them, still not resolved</span>
          </div>
          <div className="stat">
            <span className="stat-figure">{affected}</span>
            <span className="stat-label">Clients affected</span>
            <span className="stat-hint">In the alerts shown below</span>
          </div>
        </div>
      )}

      {blind > 0 && (
        <div className="callout callout-evidence al-legend">
          <span className="callout-title">
            {plural(blind, 'alert')} {blind === 1 ? 'is' : 'are'} about our visibility, not the
            client&rsquo;s security
          </span>
          A visibility alert means DriftWatch can no longer assess a control it could assess before —
          almost always a licence change or a narrowing of the consent granted for monitoring.
          Nothing has been found wrong with that tenant, and none of these is counted as a failure.
        </div>
      )}

      <div className="toolbar al-toolbar">
        <span className="label al-filter-label" id="al-state-label">
          State
        </span>
        <div className="al-chips" role="group" aria-labelledby="al-state-label">
          {STATE_FILTERS.map((option) => (
            <button
              key={option.id}
              type="button"
              className="al-chip"
              aria-pressed={stateFilter === option.id}
              onClick={() => setStateFilter(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>

        <span className="spacer" />

        <button
          type="button"
          className="btn btn-quiet btn-sm"
          aria-busy={busy || undefined}
          disabled={busy}
          onClick={() => void load(stateFilter, true)}
        >
          {busy ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <div className="toolbar al-toolbar">
        <span className="label al-filter-label" id="al-kind-label">
          Kind
        </span>
        <div className="al-chips" role="group" aria-labelledby="al-kind-label">
          {KINDS.map((kind) => (
            <button
              key={kind}
              type="button"
              className="al-chip"
              aria-pressed={kinds.has(kind)}
              onClick={() => toggleKind(kind)}
            >
              {KIND[kind].label}
              <span className="num al-chip-count">{kindCounts[kind]}</span>
            </button>
          ))}
        </div>

        {filtered && (
          <button type="button" className="btn btn-quiet btn-sm" onClick={() => setKinds(new Set())}>
            Clear kind filter
          </button>
        )}
      </div>

      {/* Only where there is something to acknowledge, and always while the
          table is still a skeleton — a field that appears once the rows land
          would push them down at the moment the reader started reading. */}
      {(loading || visible.some((alert) => alert.state === 'open')) && (
        <div className="field al-who">
          <label htmlFor="al-who-name">Your name</label>
          <input
            id="al-who-name"
            ref={whoRef}
            type="text"
            autoComplete="name"
            value={who}
            aria-describedby="al-who-hint"
            onChange={(event) => setWho(event.target.value)}
            onBlur={() => writeWho(who.trim())}
          />
          <span className="hint" id="al-who-hint">
            Recorded against every alert you acknowledge and shown to whoever opens it next. It is
            typed here rather than taken from your account, and it is remembered in this browser.
          </span>
        </div>
      )}

      {loaded && (
        <p className="status-inline al-count" role="status">
          {loading
            ? 'Reading the alerts raised by the most recent assessments…'
            : filtered
              ? `Showing ${visible.length} of ${inState.length} alerts.`
              : plural(visible.length, 'alert')}
        </p>
      )}

      {!loaded ? null : !loading && visible.length === 0 ? (
        <EmptyAlerts filtered={filtered} stateFilter={stateFilter} />
      ) : (
        <div className="tbl-wrap">
          <table className="tbl al-table">
            <thead>
              <tr>
                <th scope="col">Client</th>
                <th scope="col">Alert</th>
                <th scope="col">Severity</th>
                <th scope="col">What moved</th>
                <th scope="col">Seen</th>
                <th scope="col">Acknowledgement</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? [0, 1, 2, 3, 4].map((index) => <SkeletonRow key={index} />)
                : visible.map((alert) => (
                    <AlertRow
                      key={alert.id}
                      alert={alert}
                      now={now}
                      busy={pending === alert.id}
                      canAcknowledge={who.trim() !== ''}
                      onAcknowledge={acknowledge}
                    />
                  ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const SkeletonRow = () => (
  <tr className="skeleton-row">
    <td className="skeleton-cell" style={{ '--sk-w': '70%' } as CSSProperties} />
    <td className="skeleton-cell" style={{ '--sk-w': '85%' } as CSSProperties} />
    <td className="skeleton-cell" style={{ '--sk-w': '50%' } as CSSProperties} />
    <td className="skeleton-cell" style={{ '--sk-w': '60%' } as CSSProperties} />
    <td className="skeleton-cell" style={{ '--sk-w': '45%' } as CSSProperties} />
    <td className="skeleton-cell" style={{ '--sk-w': '55%' } as CSSProperties} />
  </tr>
);

interface VerdictWordProps {
  word: string;
}

const VerdictWord = ({ word }: VerdictWordProps) => {
  const className = VERDICT_CLASS[word];
  if (className === undefined) return <span className="mono">{word}</span>;
  return <span className={`verdict ${className}`}>{word}</span>;
};

interface SeverityBadgeProps {
  severity: Severity;
}

/** Exactly four marks, the first N filled — the contract `.sev` is built on. */
const SeverityBadge = ({ severity }: SeverityBadgeProps) => (
  <span className={`sev sev-${severity}`}>
    <span className="sev-ticks" aria-hidden="true">
      <span />
      <span />
      <span />
      <span />
    </span>
    {SEVERITY_TEXT[severity]}
  </span>
);

interface AlertRowProps {
  alert: AlertView;
  now: number;
  busy: boolean;
  canAcknowledge: boolean;
  onAcknowledge: (alert: AlertView) => void;
}

const AlertRow = ({ alert, now, busy, canAcknowledge, onAcknowledge }: AlertRowProps) => {
  const kind = KIND[alert.kind];
  const subject = subjectOf(alert);

  return (
    <tr className={`al-row ${kind.row}`}>
      <th scope="row" className="al-client">
        {alert.tenantDisplayName}
      </th>

      <td>
        <span className={`al-kind ${kind.chip}`}>{kind.label}</span>
        <span className="al-control">{subject.title}</span>
        <span className="mono al-id">{subject.id}</span>
        {/* Verbatim. The alert record, the API and this cell have to be the
            same sentence, and only the server writes it. */}
        <p className="note al-sentence">{alert.summary}</p>
      </td>

      <td>
        <SeverityBadge severity={alert.severity} />
      </td>

      <td>
        {/* Nothing moved on an excess permission, and no pair of words is put
            here to fill the column. We withdrew the request; the assignment the
            client granted stayed exactly where it was. */}
        {alert.kind === 'excess_permission' ? (
          <span className="note al-unmoved">Nothing moved — DriftWatch stopped asking for it</span>
        ) : (
          <span className="al-moved">
            <VerdictWord word={alert.before} />
            <span className="al-arrow" aria-hidden="true">
              →
            </span>
            <span className="sr-only">became</span>
            <VerdictWord word={alert.after} />
          </span>
        )}
      </td>

      <td>
        <span className="al-when">{relative(alert.lastObservedAt, now)}</span>
        <span className="note al-date num">Still true at {asDate(alert.lastObservedAt)}</span>
        <span className="note al-date num">First seen {asDate(alert.observedAt)}</span>
      </td>

      <td>
        {alert.state === 'open' ? (
          <button
            type="button"
            className="btn btn-quiet btn-sm"
            aria-disabled={canAcknowledge ? undefined : true}
            aria-busy={busy || undefined}
            aria-describedby="al-who-hint"
            aria-label={`Acknowledge ${subject.title} for ${alert.tenantDisplayName}`}
            onClick={() => onAcknowledge(alert)}
          >
            {busy ? 'Recording…' : 'Acknowledge'}
          </button>
        ) : alert.state === 'acknowledged' ? (
          <span className="al-ack">
            <span className="al-ack-by">{alert.acknowledgedBy ?? 'Acknowledged'}</span>
            {alert.acknowledgedAt !== null && (
              <span className="note num">{asDate(alert.acknowledgedAt)}</span>
            )}
          </span>
        ) : (
          <span className="al-ack">
            <span className="al-ack-by">Resolved</span>
            {alert.resolvedAt !== null && (
              <span className="note num">{asDate(alert.resolvedAt)}</span>
            )}
            <span className="note">
              {alert.kind === 'excess_permission'
                ? 'The permission is no longer granted to DriftWatch in this tenant.'
                : 'The control has moved back at a later assessment.'}
            </span>
          </span>
        )}
      </td>
    </tr>
  );
};

interface EmptyAlertsProps {
  filtered: boolean;
  stateFilter: StateFilter;
}

/**
 * No alerts is good news and the ordinary case, so this must not read as a
 * failure to load. The three silences below all look like bugs from the
 * outside — an operator who does not know about them concludes the feature is
 * broken and stops trusting the ones that do arrive.
 */
const EmptyAlerts = ({ filtered, stateFilter }: EmptyAlertsProps) => {
  if (filtered) {
    return (
      <div className="empty al-empty">
        <span className="empty-title">No alerts of that kind</span>
        <p className="empty-body">
          There are alerts in this state, but none of the kind you have selected. Clear the kind
          filter above to see them.
        </p>
      </div>
    );
  }

  if (stateFilter !== 'live' && stateFilter !== 'open') {
    return (
      <div className="empty al-empty">
        <span className="empty-title">
          {stateFilter === 'resolved' ? 'Nothing has resolved yet' : 'Nothing is acknowledged'}
        </span>
        <p className="empty-body">
          {stateFilter === 'resolved'
            ? 'An alert resolves by itself when a later assessment finds the control back where it was. None has, so far.'
            : 'Acknowledging an alert records who has picked it up. Nobody has acknowledged anything yet.'}
        </p>
      </div>
    );
  }

  return (
    <div className="empty al-empty">
      <span className="empty-title">No alerts — nothing has moved backwards</span>
      <p className="empty-body">
        An alert is raised when a control moves backwards between two assessments of the same client,
        and it appears here as soon as that assessment finishes. Three silences are deliberate, and
        none of them means this screen is broken:
      </p>
      <ul className="empty-body al-silences">
        <li>
          A client&rsquo;s <strong>first assessment</strong> raises nothing. There is no earlier
          result for anything to have moved from — at onboarding, the report is the deliverable.
        </li>
        <li>
          A control that was <strong>already not met and still is</strong> raises nothing. The
          verdict has not moved, and repeating a known failure every cycle is how a list like this
          teaches people to ignore it.
        </li>
        <li>
          A control we <strong>can no longer assess</strong> raises a quieter visibility alert rather
          than a failure — usually a licence change or a narrowed consent, never a finding about the
          client&rsquo;s security.
        </li>
      </ul>
    </div>
  );
};
